import React from "react";
import styled from "styled-components";
import {ReactComponent as HighFlierTierIcon} from "../assets/hightimes-tier-high-flyer.svg";
import {ReactComponent as FourTwentyTierIcon} from "../assets/hightimes-tier-420.svg";
import useLoyalty from "../hooks/useLoyalty";
import useSite from "../../sites/hooks/useSite";
import useAuthentication from "src/core/authentication/hooks/useAuthentication";
import Skeleton from "react-loading-skeleton";
import {ModalText} from "../../common/components/modules/modal/modalComponents";
import media from "src/core/common/themes/media";

function LoyaltyHeader({className, mode, showPoints = true}) {
  const site = useSite();
  const {loyalty, meta} = useLoyalty();
  const [, authApi] = useAuthentication();
  const user = authApi.userProfile();

  const loyaltyPointsEnabled = site && site.getOptions().loyaltyPointsEnabled();
  const loyaltyTiersEnabled = site && site.getOptions().loyaltyTiersEnabled();

  if (!loyaltyPointsEnabled && !loyaltyTiersEnabled) {
    return <></>;
  }

  if (meta.loading || !loyalty || authApi.isValidating()) {
    return (
      <div style={{width: "100%", height: "69px"}} className={className}>
        <Skeleton height={69} />
      </div>
    );
  }

  const TierIcon = getTierIcon(loyalty);

  function createMarkup(html) {
    return {__html: html};
  }

  return (
    <>
      {showPoints && loyaltyPointsEnabled && loyalty.points > 0 && (
        <SubHeader mode={mode}>
          <span>
            Hey {user.getFirstName()}, you currently have {loyalty.points} points.
          </span>
        </SubHeader>
      )}
      {loyaltyTiersEnabled && (
        <Container className={className}>
          <TransparentContainer>
            {loyaltyTiersEnabled && TierIcon && (
              <TierIconContainer>
                <TierIcon />
              </TierIconContainer>
            )}
            <div>
              {loyaltyTiersEnabled && (
                <PointsAnnouncement mode={mode}>
                  {`Hey ${user.getFirstName()}, you currently have ${
                    loyalty.tierPoints
                  } points.`}
                </PointsAnnouncement>
              )}
              {loyalty.announcement && loyaltyTiersEnabled && (
                <Description
                  mode={mode}
                  dangerouslySetInnerHTML={createMarkup(loyalty.announcement)}
                />
              )}
            </div>
          </TransparentContainer>
        </Container>
      )}
    </>
  );
}

function getTierIcon(loyalty) {
  switch (loyalty.rank) {
    case 0:
      return FourTwentyTierIcon;
    case 1:
      return HighFlierTierIcon;
    default:
      return null;
  }
}

const TierIconContainer = styled.div`
  height: 51px;
  width: 51px;
  margin-right: ${({theme}) => theme.v2.spacing(2)};
  > * {
    height: 51px;
    width: 51px;
  }
`;

const SubHeader = styled(ModalText)`
  height: ${({theme}) => theme.v2.spacing(2)};
  margin-bottom: ${({theme}) => theme.v2.spacing(8)};
  ${(mode) => (mode === "small" ? "font-size: 12px !important;" : "")}
`;

const Container = styled.div`
  width: 100%;
  margin-bottom: 32px;
  border-radius: ${({theme}) => theme.v2.decoration.border.radius.default};
  background-color: ${({theme}) => theme.v2.color.primary};
`;
const TransparentContainer = styled.div`
  display: flex;
  align-items: center;
  border-radius: ${({theme}) => theme.v2.decoration.border.radius.default};
  border: 1px solid ${({theme}) => theme.v2.color.primary};
  background-color: #fff;
  opacity: 0.9;
  padding: ${({theme}) => theme.v2.spacing(2)};
`;

const PointsAnnouncement = styled.div`
  color: ${({theme}) => theme.v2.color.onSurface};
  font-weight: ${({theme}) => theme.v2.typography.body.weight.heavy};
  font-family: ${({theme}) => theme.v2.typography.bodyText1.family};
  font-size: ${({theme, mode}) =>
    mode === "small" ? "12px" : theme.v2.typography.sizing.l.lg};
  line-height: calc(
    6px +
      ${({theme, mode}) =>
        mode === "small"
          ? theme.v2.typography.sizing.s.lg
          : theme.v2.typography.sizing.l.lg}
  );

  ${media.down("md")} {
    line-height: calc(
      4px +
        ${({theme, mode}) =>
          mode === "small"
            ? theme.v2.typography.sizing.s.lg
            : theme.v2.typography.sizing.l.lg}
    );
  }
`;
const Description = styled.div`
  color: ${({theme}) => theme.v2.color.base.grey["900"]};
  font-weight: ${({theme}) => theme.v2.typography.bodyText1.weight.default};

  font-size: ${({theme, mode}) =>
    mode === "small" ? "12px" : theme.v2.typography.sizing.m.lg};
  line-height: calc(
    6px +
      ${({theme, mode}) => (mode === "small" ? "12px" : theme.v2.typography.sizing.m.lg)}
  );

  ${media.down("md")} {
    font-size: 12px;
    line-height: calc(4px + ${({theme}) => theme.v2.typography.sizing.s.lg});
    margin-top: ${({theme, mode}) => (mode === "small" ? 0 : theme.v2.spacing(2))};
  }
`;

export default LoyaltyHeader;
